// DayEventsPage — список событий конкретного дня (R32 / U-8).
// Маршрут: /calendar/day/:date (date в формате yyyy-MM-dd).
// Раньше клик по дню вёл на /calendar/events/<дата> — дата парсилась как UUID → 400.
// FIX-C (MEDIUM S1/S2/S4): границы дня — локальные полночь/конец дня → UTC Instant
// (иначе события локальной ночи 00:00–06:00 UTC+3 уезжали в прошлый день), а
// date-параметр валидируется через isValid(parseISO) — «2026-99-99» даёт
// «Некорректная дата» вместо RangeError.
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { format, parseISO, isValid, startOfDay, endOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { calendarApi, type CalendarEvent } from '@/api/calendar';

/** Формат path-параметра даты (yyyy-MM-dd). */
const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Размер страницы: все события дня одним запросом (KISS). */
const DAY_PAGE_SIZE = 200;

/** Страница событий одного дня календаря. */
export function DayEventsPage(): JSX.Element {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();
  // Regex + isValid: «2026-99-99» проходит по формату, но не является датой —
  // без isValid формат(title) бросил бы RangeError (FIX-C).
  const validDate =
    date && DATE_PARAM_PATTERN.test(date) && isValid(parseISO(date)) ? date : null;

  // Локальная полночь/конец дня → UTC Instant (Europe/Moscow: 02.09 = 01.09T21:00Z…
  // 02.09T20:59:59.999Z) — события ночи попадают в правильный день (FIX-C).
  const dayBounds = validDate
    ? {
        from: startOfDay(parseISO(validDate)).toISOString(),
        to: endOfDay(parseISO(validDate)).toISOString(),
      }
    : null;

  const query = useQuery({
    queryKey: ['calendar-day', validDate],
    queryFn: () =>
      calendarApi.list({
        from: dayBounds?.from ?? '',
        to: dayBounds?.to ?? '',
        size: DAY_PAGE_SIZE,
      }),
    enabled: Boolean(validDate),
  });

  if (!validDate) {
    return (
      <EmptyState
        title="Некорректная дата"
        description="Ожидается дата в формате ГГГГ-ММ-ДД"
        actionLabel="К календарю"
        onAction={() => navigate('/calendar')}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title={format(parseISO(validDate), 'd MMMM yyyy', { locale: ru })}
        subtitle="События дня"
        actions={
          <Button
            component={RouterLink}
            to={`/calendar/new?date=${validDate}`}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Создать событие
          </Button>
        }
      />
      {query.isLoading ? (
        <LoadingSpinner label="Загрузка событий…" />
      ) : query.isError ? (
        <EmptyState
          title="Не удалось загрузить события"
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        />
      ) : !query.data || query.data.content.length === 0 ? (
        <EmptyState
          title="Нет событий"
          description="В этот день событий нет"
          actionLabel="К календарю"
          onAction={() => navigate('/calendar')}
        />
      ) : (
        <Stack spacing={1}>
          {query.data.content.map((ev: CalendarEvent) => (
            <Paper
              key={ev.id}
              onClick={() => navigate(`/calendar/events/${ev.id}`)}
              sx={{ p: 1.5, cursor: 'pointer' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {ev.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(new Date(ev.startAt), 'HH:mm', { locale: ru })} —{' '}
                {format(new Date(ev.endAt), 'HH:mm', { locale: ru })}
                {ev.location ? ` · ${ev.location}` : ''}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
